/**
 * Mobile Drawer Component
 * Industry-standard mobile navigation with Tailwind CSS
 * Fully responsive: Mobile, Tablet, Desktop
 */

import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  X,
  Home,
  BarChart3,
  Users,
  Building2,
  Settings,
  LogOut,
  Activity,
  Shield,
  TrendingUp,
  Mail,
  Target,
  DollarSign,
  FileText,
  FileSearch,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useDataSourceStore } from '../../store/dataSourceStore';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileDrawer: React.FC<MobileDrawerProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { selectedCompany } = useDataSourceStore();

  // Check if BetterStack module is available
  const { data: companyData } = useQuery({
    queryKey: ['company', selectedCompany?._id],
    queryFn: async () => {
      if (!selectedCompany?._id) return null;
      const response = await api.get(`/api/v1/companies/${selectedCompany._id}`);
      return response.data.data;
    },
    enabled: !!selectedCompany?._id,
    staleTime: 5 * 60 * 1000,
  });

  const hasBetterStackModule = companyData?.dataSources?.some(
    (ds: any) => ds.isActive && ds.availableModules?.includes('betterstack')
  );

  // Close drawer on route change
  React.useEffect(() => {
    if (isOpen) {
      onClose();
    }
  }, [location.pathname]);

  // Prevent body scroll when drawer is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = '0';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen]);

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
    onClose();
  };

  const isActive = (path: string) => location.pathname === path;

  // Helper function to check if user has permission for a resource
  const hasPermission = (resource: string, action: string = 'read'): boolean => {
    if (!user || !user.roles || user.roles.length === 0) {
      return false;
    }
    
    // Check if roles are just strings (IDs) instead of objects
    if (typeof user.roles[0] === 'string') {
      console.error('❌ Roles not populated. Please log out and log in again.');
      return false;
    }
    
    // Super admin check
    const isSuperAdmin = user.roles.some((role: any) => 
      role.permissions?.some((perm: any) => 
        perm.resource === '*' && perm.actions.includes('*')
      )
    );
    if (isSuperAdmin) return true;
    
    // Check specific permission
    return user.roles.some((role: any) =>
      role.permissions?.some((perm: any) =>
        (perm.resource === resource || perm.resource === '*') &&
        (perm.actions.includes(action) || perm.actions.includes('*'))
      )
    );
  };

  const menuItems = [
    { icon: Home, label: 'Dashboard', path: '/dashboard', permission: 'dashboards' },
    { icon: BarChart3, label: 'Analytics', path: '/analytics', permission: 'analytics' },
    { icon: TrendingUp, label: 'Funnel Analysis', path: '/analytics/funnel', permission: 'funnel' },
    { icon: Target, label: 'Attribution', path: '/analytics/attribution', permission: 'attribution' },
    { icon: DollarSign, label: 'ROAS Forecast', path: '/analytics/roas', permission: 'roas' },
    { icon: Activity, label: 'Sessions', path: '/sessions', permission: 'sessions' },
    { icon: Mail, label: 'Email Campaigns', path: '/email-campaigns', permission: 'email-campaigns' },
    { icon: FileText, label: 'Email Logs', path: '/email-logs', permission: 'email-logs' },
    ...(hasBetterStackModule && hasPermission('betterstack')
      ? [{ icon: FileSearch, label: 'BetterStack', path: '/betterstack', permission: 'betterstack' }]
      : []),
    { icon: Users, label: 'Users', path: '/admin/users', permission: 'users', adminOnly: true },
    { icon: Shield, label: 'Roles', path: '/admin/roles', permission: 'roles', adminOnly: true },
    { icon: Building2, label: 'Companies', path: '/admin/companies', permission: 'companies', adminOnly: true },
    { icon: Settings, label: 'Settings', path: '/settings', permission: 'settings' },
  ].filter(item => !item.permission || hasPermission(item.permission));

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop Overlay - Animated */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] lg:hidden
                   animate-in fade-in duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer Panel - Slide from left */}
      <div
        className="fixed top-0 left-0 bottom-0 z-[9999] lg:hidden
                   w-full max-w-[280px] sm:max-w-[320px]
                   bg-white dark:bg-gray-900
                   shadow-2xl
                   animate-in slide-in-from-left duration-300
                   flex flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        {/* Header */}
        <div className="flex-none bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-5">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-white truncate">Analytics</h2>
              <p className="text-sm text-indigo-100 truncate mt-0.5">{user?.email}</p>
            </div>
            <button
              onClick={onClose}
              className="flex-none p-2 rounded-lg bg-white/10 hover:bg-white/20 
                       text-white transition-colors duration-200"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* User Info Card */}
        <div
          className="flex-none mx-4 mt-4 p-4 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 
                      dark:from-indigo-950 dark:to-purple-950 border border-indigo-100 dark:border-indigo-800"
        >
          <div className="flex items-center gap-3">
            <div
              className="flex-none w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
                          flex items-center justify-center text-white font-semibold text-lg shadow-lg"
            >
              {user?.firstName?.[0]}
              {user?.lastName?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 dark:text-white truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                {user?.roles?.[0]?.name || 'User'}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Menu - Scrollable */}
        <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
          {menuItems.map(item => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center gap-3 px-4 py-3.5 rounded-xl font-medium text-sm
                  transition-all duration-200
                  ${
                    active
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30 scale-[1.02]'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }
                `}
              >
                <Icon
                  size={20}
                  className={active ? 'text-white' : 'text-gray-500 dark:text-gray-400'}
                />
                <span className="flex-1">{item.label}</span>
                {active && <div className="w-2 h-2 rounded-full bg-white animate-pulse" />}
              </Link>
            );
          })}
        </nav>

        {/* Footer - Logout */}
        <div
          className="flex-none p-4 border-t border-gray-200 dark:border-gray-800 
                      bg-gray-50 dark:bg-gray-900/50"
        >
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-3 px-4 py-3.5 
                     rounded-xl font-medium text-sm
                     text-red-600 dark:text-red-400 
                     hover:bg-red-50 dark:hover:bg-red-900/20
                     transition-all duration-200 border-2 border-transparent
                     hover:border-red-200 dark:hover:border-red-800"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileDrawer;
